import { Alert, Button, Input, Prompt, TextTooltip } from '@components'
import { useBusinessContext } from '@context'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import {
  deleteExpenseClaimRate,
  expenseClaimRateError,
  ExpenseClaimRate,
  ExpenseRateUnit,
  fetchExpenseClaimRates,
  saveExpenseClaimRate
} from '@query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaPen, FaTrash } from 'react-icons/fa6'
import styled from 'styled-components'

const euroFormatter = new Intl.NumberFormat('fi-FI', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const queryKey = (slug: string) => [slug, 'expense-claim-rates']

const formatSigned = (amount: string, isSupplement: boolean) => {
  const formatted = euroFormatter.format(Number(amount))
  return isSupplement ? `+${formatted}` : formatted
}

const amountForInput = (amount: string | null) => (amount ? amount.replace('.', ',') : '')

const amountForApi = (value: string) => value.trim().replace(/\s/g, '').replace(',', '.')

export const ExpenseClaimRatesSettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [editing, setEditing] = useState<ExpenseClaimRate | null>(null)
  const [draft, setDraft] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKey(slug),
    queryFn: () => fetchExpenseClaimRates(slug)
  })

  const save = useMutation({
    mutationFn: (rate: ExpenseClaimRate) =>
      saveExpenseClaimRate(slug, rate.rate_type, amountForApi(draft)),
    onSuccess: next => {
      queryClient.setQueryData(queryKey(slug), next)
      setEditing(null)
      setFieldError(null)
    },
    onError: error => {
      setFieldError(expenseClaimRateError(error) ?? t('general.error'))
    }
  })

  const remove = useMutation({
    mutationFn: (rateType: string) => deleteExpenseClaimRate(slug, rateType),
    onSuccess: next => {
      queryClient.setQueryData(queryKey(slug), next)
    }
  })

  const openEditor = (rate: ExpenseClaimRate) => {
    setEditing(rate)
    setDraft(amountForInput(rate.own_amount))
    setFieldError(null)
  }

  const formatRate = (rate: ExpenseClaimRate, amount: string) => {
    const signed = formatSigned(amount, rate.is_supplement)
    if (rate.unit === 'per_km_per_person') {
      return t('settings.expenseClaimRates.perPersonValue', { amount: signed })
    }
    if (rate.unit === 'per_day') {
      return t('settings.expenseClaimRates.perDayValue', { amount: signed })
    }
    return t('settings.expenseClaimRates.perKmValue', { amount: signed })
  }

  const rateInputLabel = (unit: ExpenseRateUnit) => {
    if (unit === 'per_km_per_person') return t('settings.expenseClaimRates.ratePerPerson')
    if (unit === 'per_day') return t('settings.expenseClaimRates.ratePerDay')
    return t('settings.expenseClaimRates.ratePerKm')
  }

  const allowanceName = (rateType: string) => t(`settings.expenseClaimRates.rates.${rateType}`)

  return (
    <SettingsSubPageLayout
      title={t('settings.sections.expenseClaims')}
      subTitle={data ? t('settings.expenseClaimRates.intro', { year: data.year }) : ''}
    >
      {isLoading && <p>{t('general.loading')}</p>}
      {isError && <Alert type="error" description={t('settings.expenseClaimRates.loadError')} />}
      {data && (
        <>
          {data.rates_outdated_for_year && (
            <Alert
              type="warning"
              description={t('settings.expenseClaimRates.outdated', { year: data.year })}
            />
          )}
          <Table>
            <thead>
              <tr>
                <th>{t('settings.expenseClaimRates.allowance')}</th>
                <th>{t('settings.expenseClaimRates.taxFree', { year: data.year })}</th>
                <th>{t('settings.expenseClaimRates.companyRate')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rates.map(rate => (
                <tr key={rate.rate_type}>
                  <td>{allowanceName(rate.rate_type)}</td>
                  <td>{formatRate(rate, rate.tax_free_amount)}</td>
                  <td>
                    <RateActions>
                      {rate.own_amount ? <span>{formatRate(rate, rate.own_amount)}</span> : null}
                      <TextTooltip
                        tooltip={t(
                          rate.own_amount
                            ? 'settings.expenseClaimRates.edit'
                            : 'settings.expenseClaimRates.set'
                        )}
                        placement="bottom-end"
                      >
                        <Button
                          type="button"
                          icon={<FaPen />}
                          size="sm"
                          onClick={() => openEditor(rate)}
                        />
                      </TextTooltip>
                      {rate.own_amount ? (
                        <TextTooltip
                          tooltip={t('settings.expenseClaimRates.remove')}
                          placement="bottom-end"
                        >
                          <Button
                            type="button"
                            icon={<FaTrash />}
                            size="sm"
                            intent="danger"
                            showSpinner={remove.isPending && remove.variables === rate.rate_type}
                            onClick={() => remove.mutate(rate.rate_type)}
                          />
                        </TextTooltip>
                      ) : null}
                    </RateActions>
                    {rate.own_rate_exceeds_tax_free && (
                      <Exceeds>
                        {t('settings.expenseClaimRates.exceeds', {
                          year: data.year,
                          amount: formatRate(rate, rate.applied_amount)
                        })}
                      </Exceeds>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Note>
            <Trans i18nKey="settings.expenseClaimRates.note">
              <NoteLink
                href={t('settings.expenseClaimRates.taxTableUrl')}
                target="_blank"
                rel="noreferrer"
              />
            </Trans>
          </Note>
        </>
      )}
      <Prompt
        isVisible={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editing
            ? t('settings.expenseClaimRates.modalTitle', {
                allowance: allowanceName(editing.rate_type)
              })
            : ''
        }
        description={
          editing ? (
            <Input
              label={rateInputLabel(editing.unit)}
              value={draft}
              inputMode="decimal"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDraft(event.target.value)
                setFieldError(null)
              }}
              errors={fieldError ? { message: fieldError } : undefined}
            />
          ) : null
        }
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setEditing(null)
          },
          {
            text: t('general.save'),
            intent: 'success',
            showSpinner: save.isPending,
            disabled: save.isPending,
            action: () => editing && save.mutate(editing)
          }
        ]}
      />
    </SettingsSubPageLayout>
  )
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.neutralWhite};

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
    vertical-align: middle;
  }

  th {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
    color: ${({ theme }) => theme.colors.iconGrayDark};
    font-weight: 600;
  }
`

const RateActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Exceeds = styled.p`
  margin: ${({ theme }) => theme.spacing.xs}rem 0 0;
  color: ${({ theme }) => theme.colors.warning};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const Note = styled.p`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  color: ${({ theme }) => theme.colors.iconGrayDark};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
`

const NoteLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`
